import { CodeMapSet, CodeMapType } from "../../codemaps";
import { MemberSetModel } from "../../members";
import { AccessCriteriaGranularityEnum, TemplateCollectionMetadata, TemplateService, TemplateSpecialtyGroup } from "../../templates";
import { Roles } from "../../user";
import { TimeDistanceCalculatorType } from "../enumerators";
import Project from "../project";

export class InitialProjectState {
  excludeDeactivatedFacilityImpact: boolean;
  excludeDeactivatedFacilityNetwork: boolean;
  excludeDeactivatedProviderImpact: boolean;
  excludeDeactivatedProviderNetwork: boolean;
  excludeInvalidNppesNpiFacilityImpact: boolean;
  excludeInvalidNppesNpiFacilityNetwork: boolean;
  excludeInvalidNppesNpiProviderImpact: boolean;
  excludeInvalidNppesNpiProviderNetwork: boolean;
  excludeMedicareOptOutImpact: boolean;
  excludeOIGExclusionImpact: boolean;
  excludePrecludedImpact: boolean;
  excludeMedicareOptOutNetwork: boolean;
  excludeOIGExclusionNetwork: boolean;
  excludePrecludedNetwork: boolean;
  project: Project;
  timeDistanceCalculatorTypes?: TimeDistanceCalculatorType[];
}

export interface TemplateInfo {
  attestationCrosswalkCodeMapSetId: number,
  motiveCrosswalkCodeMapSetId: number,
  projectAccessCodeMapSets: CodeMapSet[],
  projectMemberPopulations: MemberSetModel[],
  specialtyGroups: TemplateSpecialtyGroup[],
  templateAccessGranularity: AccessCriteriaGranularityEnum,
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[],
}

export class EditTemplateOptionsHelper {
  private readonly templateService = new TemplateService();

  public async getTemplateInfo(
    initialProjectState: InitialProjectState,
    allAccessCodeMapSets: CodeMapSet[],
    allMemberPopulations: MemberSetModel[],
    templateCollections: TemplateCollectionMetadata[],
    templateCollectionId: number,
    templateCorrelationId: string,
    templateId: string): Promise<TemplateInfo> {
    // Setup the retrieval of the specialty groups while the member sets and code map sets are fetched.

    let specialtyGroupsPromise;
    let templateMetadataPromise;

    if (!templateId || (initialProjectState && (templateCollectionId !== initialProjectState.project.adequacyTemplateCollectionId ||
      templateCorrelationId !== initialProjectState.project.adequacyTemplateCorrelationId))) {
      //get the latest template if template collection or template correlation id changes
      specialtyGroupsPromise = this.templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(templateCollectionId, templateCorrelationId);
      templateMetadataPromise =
        this.templateService.getLatestTemplateByCorrelationId(templateCollectionId, templateCorrelationId, true);
    } else {
      specialtyGroupsPromise = this.templateService.getTemplateSpecialtyGroups(templateCollectionId, templateId);
      templateMetadataPromise = this.templateService.getTemplateById(templateCollectionId, templateId, true);
    }

    let projectAccessCodeMapSets: CodeMapSet[] = allAccessCodeMapSets;
    let projectMemberPopulations: MemberSetModel[] = allMemberPopulations;
    let motiveCrosswalkCodeMapSetId: number = null;

    // If the first selection in the dropdown list of correlation IDs is chosen, we need to clear the access codeMap options
    if (templateCorrelationId === "") {
      projectAccessCodeMapSets = [];
      projectMemberPopulations = [];
    } else {
      // Get to the template, using the chosen collection, and the correlation ID
      for (let collection of templateCollections) {
        if (collection.templateCollectionId === templateCollectionId) {
          // Populate the collection with template information
          collection = await this.templateService.getTemplateCollectionById(collection.templateCollectionId);

          // Found the template collection
          for (const template of collection.templates) {
            if (template.correlationId === templateCorrelationId) {
              // Found the template - now filter down the access codeMaps available for selection to those that are compatible with the chosen template
              if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.ZipClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.ZipCodeClassMap);
              } else if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.CountyClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.CountyClassMap);
              } else {
                projectAccessCodeMapSets = [];
              }

              // Suggest the codemapset and memberset that are specified by the template, by moving them to the top of their respective list
              if (projectAccessCodeMapSets && projectAccessCodeMapSets.length > 0 && template.accessCodeMapSetId !== null && template.accessCodeMapSetId !== undefined) {
                projectAccessCodeMapSets.sort((x, y) => x.codeMapSetId === template.accessCodeMapSetId ? -1 : y.codeMapSetId === template.accessCodeMapSetId ? 1 : 0);
              }
              if (projectMemberPopulations && projectMemberPopulations.length > 0 && template.memberSetId !== null && template.memberSetId !== undefined) {
                projectMemberPopulations.sort((x, y) => x.memberSetId === template.memberSetId ? -1 : y.memberSetId === template.memberSetId ? 1 : 0);
              }
              if (template.motiveCrosswalkCodeMapSetId !== undefined && template.motiveCrosswalkCodeMapSetId !== null) {
                motiveCrosswalkCodeMapSetId = template.motiveCrosswalkCodeMapSetId;
              }
            }
          }
        }
      }
    }

    const templateMetadata = await templateMetadataPromise;
    const specialtyGroups = await specialtyGroupsPromise;

    return {
      attestationCrosswalkCodeMapSetId: templateMetadata.attestationCrosswalkCodeMapSetId,
      motiveCrosswalkCodeMapSetId,
      projectAccessCodeMapSets,
      projectMemberPopulations,
      specialtyGroups,
      templateAccessGranularity: templateMetadata.accessCriteriaSpecificationGranularity,
      timeDistanceCalculatorTypes: templateMetadata.timeDistanceCalculatorTypes,
    } as TemplateInfo;
  }

  public getTimeDistanceCalculatorType(templateInfo: TemplateInfo, userRoles: string[], timeDistanceCalculatorType: TimeDistanceCalculatorType): TimeDistanceCalculatorType {
    const roleCalculatorTypeMap: { [key: string]: TimeDistanceCalculatorType } = {
      [Roles.GEOGRAPHY_AWARE_DISTANCE_USER]: TimeDistanceCalculatorType.GeographyAware,
      [Roles.ROUTING_DISTANCE_USER]: TimeDistanceCalculatorType.GraphhopperRouting,
      [Roles.STRAIGHT_LINE_DISTANCE_USER]: TimeDistanceCalculatorType.StraightLine,
    };

    const filteredTypes = templateInfo.timeDistanceCalculatorTypes.filter(t =>
      t === TimeDistanceCalculatorType.Quest || userRoles.find(r => roleCalculatorTypeMap[r] === t));

    return (filteredTypes && filteredTypes.length > 0)
      ? timeDistanceCalculatorType
        ? filteredTypes.find(t => t === timeDistanceCalculatorType)
          ? filteredTypes.find(t => t === timeDistanceCalculatorType)
          : filteredTypes[0]
        : filteredTypes[0]
      : null
  }

  public getTemplateGranularityPrefix(templateAccessGranularity: AccessCriteriaGranularityEnum): string {
    switch (templateAccessGranularity) {
      case AccessCriteriaGranularityEnum.CountyClass:
        return "County";
      case AccessCriteriaGranularityEnum.ZipClass:
        return "Zip";
      default:
        return "Service Area";
    }

  }
} 